import * as React from "react"
import {
  autenticacaoToken as autenticacaoTokenService,
  validarToken as validarTokenService,
  plataformaAutenticacaoToken as plataformaToken,
  plataformaLogin as plataformaAuth,
  plataformaResetPass,
  requestPasswordReset,
  validateOtp,
  resetPassword,
  refreshToken as refreshTokenService,
  endImpersonationRequest,
  ImpersonationUser,
} from "@/modules/Auth/service/AuthServices"
import {
  USER_UPDATED_EVENT,
  IMPERSONATION_ENDED_EVENT,
  IMPERSONATION_SESSION_KEY,
  isImpersonating as checkIsImpersonating,
  restoreAdminSession,
} from "@/api/axios.config"
import { MembroDataType } from "@/types/membros"
import { useToast } from "@/contexts/ToastContext"

type AuthContextType = {
  user: MembroDataType | null
  token: string | null
  login: (user: MembroDataType, token: string) => void
  logout: () => void
  refreshUser: () => void
  isAuthenticated: () => boolean
  autenticacaoToken: (email: string) => Promise<any>
  plataformaLogin: (email: string, valor?: string, tipo?: string, token?: string) => Promise<void>
  plataformaResetSenha: (email: string, nova_senha: string, token?: string) => Promise<void>
  plataformaAutenticacaoToken: (email: string) => Promise<any>
  validarToken: (email: string, otp: string) => Promise<void>
  loginWithFaceId: (storedRefreshToken: string, savedUser: MembroDataType) => Promise<void>
  // Novos métodos adicionados
  handleRequestReset: (email: string) => Promise<void>
  handleValidateOtp: (email: string, otp: string) => Promise<string>
  handleConfirmReset: (resetToken: string, novaSenha: string) => Promise<void>
  // Personificação
  isImpersonating: boolean
  startImpersonation: (impersonationToken: string, impersonatedUserData: ImpersonationUser) => void
  endImpersonationSession: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<MembroDataType | null>(null)
  const [token, setToken] = React.useState<string | null>(null)
  const [isImpersonating, setIsImpersonating] = React.useState(false)
  const toast = useToast()

  const login = (user: MembroDataType, token: string) => {
    setUser(user)
    setToken(token)
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem("token")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("user")
    // faceIdRefreshToken e faceIdUser são preservados intencionalmente para permitir
    // login via Face ID após logout
  }

  const refreshUser = () => {
    const storedUser = localStorage.getItem("user")
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
  }

  const autenticacaoToken = async (email: string) => {
    return autenticacaoTokenService(email)
  }

  const validarToken = async (email: string, otp: string) => {
    return validarTokenService(email, otp)
      .then(({ data }) => {
        const user = data.data[0]
        setUser(user)
        localStorage.setItem("user", JSON.stringify(user))
      })
      .catch((error) => {
        toast.danger(error.response.data.message)
        throw new Error("Código inválido")
      })
  }

  const plataformaAutenticacaoToken = async (email: string) => {
    return plataformaToken(email)
      .then((res) => {
        toast.success("Código enviado para seu email")
        return res
      })
      .catch((error) => {
        toast.danger(error.response.data.message)
        throw new Error("Erro ao enviar código")
      })
  }

  const saveFaceIdCredentials = (refreshToken: string, user: MembroDataType) => {
    localStorage.setItem("faceIdRefreshToken", refreshToken)
    localStorage.setItem("faceIdUser", JSON.stringify(user))
  }

  const plataformaLogin = async (email: string, valor?: string, tipo?: string, token?: string) => {
    return plataformaAuth(email, valor, tipo, token)
      .then(({ data }) => {
        const apiData = data.data
        const currentUserData = apiData.user
        const userToken = apiData.token.Bearer
        const userRefreshToken = apiData.token.refreshToken

        setUser(currentUserData)
        localStorage.setItem("user", JSON.stringify(currentUserData))
        localStorage.setItem("token", userToken)
        localStorage.setItem("refreshToken", userRefreshToken)
        setToken(userToken)
        toast.success("Login realizado com sucesso!")
      })
      .catch((error) => {
        toast.danger(error.response.data.message)
        throw new Error("Código inválido")
      })
  }

  const loginWithFaceId = async (storedRefreshToken: string, savedUser: MembroDataType) => {
    try {
      const { data } = await refreshTokenService(storedRefreshToken)
      const newToken: string = data?.data?.token
      const newRefreshToken: string = data?.data?.refreshToken

      if (!newToken || !newRefreshToken) throw new Error("Token inválido")

      setUser(savedUser)
      setToken(newToken)
      localStorage.setItem("token", newToken)
      localStorage.setItem("refreshToken", newRefreshToken)
      localStorage.setItem("user", JSON.stringify(savedUser))
      saveFaceIdCredentials(newRefreshToken, savedUser)
      toast.success("Login realizado com sucesso!")
    } catch (error: any) {
      toast.danger("Sessão expirada. Por favor, faça login novamente.")
      throw error
    }
  }

  const plataformaResetSenha = async (email: string, nova_senha: string, token?: string) => {
    return plataformaResetPass(email, nova_senha, token)
      .then(() => {
        toast.success("Senha alterada com sucesso")
      })
      .catch((error) => {
        toast.danger(error.response.data.message)
        throw new Error("Erro ao resetar")
      })
  }

  // --- ADIÇÃO DOS NOVOS ENDPOINTS ---
  const handleRequestReset = async (email: string) => {
    try {
      await requestPasswordReset(email)
      toast.success("Código de recuperação enviado!")
    } catch (error: any) {
      toast.danger(error.response?.data?.message || "Erro ao solicitar recuperação")
      throw error
    }
  }

  const handleValidateOtp = async (email: string, otp: string) => {
    try {
      const { data } = await validateOtp(email, otp)
      return data.data.resetToken
    } catch (error: any) {
      toast.danger(error.response?.data?.message || "Código inválido")
      throw error
    }
  }

  const handleConfirmReset = async (resetToken: string, novaSenha: string) => {
    try {
      const { data } = await resetPassword(resetToken, novaSenha)

      setUser(data.data.user)
      localStorage.setItem("user", JSON.stringify(data.data.user))
      localStorage.setItem("token", data.data.token.Bearer)
      localStorage.setItem("refreshToken", data.data.token.refreshToken)
      setToken(data.data.token.Bearer)
      toast.success("Senha redefinida com sucesso! Seja bem vindo(a) novamente.")

      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error: any) {
      toast.danger(error.response?.data?.message || "Erro ao redefinir senha")
      throw error
    }
  }

  const isAuthenticated = () => {
    return !!user && !!token
  }

  const startImpersonation = (impersonationToken: string, impersonatedUserData: ImpersonationUser) => {
    const currentToken = localStorage.getItem("token") ?? token
    const storedUserRaw = localStorage.getItem("user")
    const currentUser: MembroDataType | null = storedUserRaw ? JSON.parse(storedUserRaw) : user

    localStorage.setItem(
      IMPERSONATION_SESSION_KEY,
      JSON.stringify({
        user: currentUser,
        token: currentToken,
        refreshToken: localStorage.getItem("refreshToken"),
      })
    )

    const impersonatedUser: MembroDataType = {
      ...(currentUser as MembroDataType),
      pesId: impersonatedUserData.pesId,
      pes_id: impersonatedUserData.pesId,
      pesUid: impersonatedUserData.pesUid,
      pes_uid: impersonatedUserData.pesUid,
      pesNome: impersonatedUserData.pesNome,
      pes_nome: impersonatedUserData.pesNome,
      pesEmail: impersonatedUserData.pesEmail,
      pes_email: impersonatedUserData.pesEmail,
      pesFotoUrl: impersonatedUserData.pesFotoUrl,
      pes_foto_url: impersonatedUserData.pesFotoUrl,
      pes_cpf: impersonatedUserData.pesCpf,
      pesTipo: impersonatedUserData.pesTipo,
      pes_tipo: impersonatedUserData.pesTipo,
      empId: impersonatedUserData.empId,
      emp_id: impersonatedUserData.empId,
      empNome: impersonatedUserData.empNome,
      emp_uid: impersonatedUserData.empUid,
      pesAdmin: impersonatedUserData.pesAdmin,
      pes_admin: impersonatedUserData.pesAdmin,
      gestaoApp: impersonatedUserData.gestaoApp,
    }

    localStorage.setItem("token", impersonationToken)
    localStorage.setItem("user", JSON.stringify(impersonatedUser))
    setUser(impersonatedUser)
    setToken(impersonationToken)
    setIsImpersonating(true)
  }

  const endImpersonationSession = async () => {
    try {
      await endImpersonationRequest()
    } catch {
    } finally {
      restoreAdminSession()
    }
  }

  React.useEffect(() => {
    const loadStoredData = () => {
      const storedToken = localStorage.getItem("token")
      const storedUser = localStorage.getItem("user")
      if (storedToken && storedUser) {
        setToken(storedToken)
        setUser(JSON.parse(storedUser))
      }
      setIsImpersonating(checkIsImpersonating())
    }
    loadStoredData()

    const handleImpersonationEnded = () => {
      refreshUser()
      const storedToken = localStorage.getItem("token")
      if (storedToken) setToken(storedToken)
      setIsImpersonating(false)
    }

    window.addEventListener(USER_UPDATED_EVENT, refreshUser)
    window.addEventListener(IMPERSONATION_ENDED_EVENT, handleImpersonationEnded)
    return () => {
      window.removeEventListener(USER_UPDATED_EVENT, refreshUser)
      window.removeEventListener(IMPERSONATION_ENDED_EVENT, handleImpersonationEnded)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        refreshUser,
        plataformaAutenticacaoToken,
        plataformaResetSenha,
        plataformaLogin,
        loginWithFaceId,
        autenticacaoToken,
        validarToken,
        isAuthenticated,
        handleRequestReset,
        handleValidateOtp,
        handleConfirmReset,
        isImpersonating,
        startImpersonation,
        endImpersonationSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
